import { inject } from '@angular/core';
import { CanActivateFn, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { catchError, map, of } from 'rxjs';
import { AuthService } from '../auth/auth.service';

const ROUTE_MODULE_MAP: { module: string; route: string }[] = [
  { module: 'dashboard_menu',         route: '/dashboard' },
  { module: 'human_resource_menu',    route: '/HR/employee' },
  { module: 'learning_management',    route: '/learning-management/training/TrainingCategory' },
  { module: 'document_control_menu',  route: '/document-control/drive' },
  { module: 'asset_management_menu',  route: '/asset-management/alerts' },
  { module: 'task_management_menu',   route: '/task-management/my-tasks' },
  { module: 'authenticator_menu',     route: '/authenticator' },
  { module: 'master_data_menu',       route: '/master-data' },
  { module: 'notification_menu',            route: '/notification' },
];

function getFirstAllowedRoute(authService: AuthService, currentUrl?: string): string | null {
  const allowed = authService.allowedModules();
  return (
    ROUTE_MODULE_MAP.find(
      ({ module, route }) =>
        allowed.includes(module) && (!currentUrl || !currentUrl.startsWith(route)),
    )?.route ?? null
  );
}

export const authGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  const authService = inject(AuthService);
  const router      = inject(Router);

  const data              = route.data as Record<string, string>;
  const hasPermissionData = !!(data && Object.keys(data).length > 0);

  const denied = () => {
    const firstRoute = getFirstAllowedRoute(authService, state.url);
    if (!firstRoute) return true;
    return router.createUrlTree([firstRoute]);
  };

  const proceedWithPermissions = () => {
    const user = authService.getUser();
    if (user?.business_status === 'inprogress') {
      if (state.url.includes('subscriptions/request')) return of(true);
      return of(true);
    }

    if (!hasPermissionData) return of(true);

    if (authService.permissionsLoaded()) {
      if (!authService.allowedModules().length) {
        authService.clearPermissions();
      } else {
        return of(authService.validatePermission(data) ? true : denied());
      }
    }

    return authService.loadPermissions().pipe(
      map(() => (authService.validatePermission(data) ? true : denied())),
      catchError(() => of(true)),
    );
  };

  return proceedWithPermissions();
};
